import { LoadingSpinner } from "components/LoadingSpinner";
import useDocumentTitle from "hooks/useDocumentTitle";
import RuleHistory from "pages/PayerRules/components/RuleHistory";
import { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useGetPackageRulesetByIdQuery } from "store/queries/packageRuleset";

import { BundledCptCodes } from "./components/BundledCptCodes";
import { ContractLanguageField } from "./components/ContractLanguageField";
import { Header } from "./components/Header";
import { PackageCalculationExamples } from "./components/PackageCalculationExamples";
import { ProviderAndPayerEditSection } from "./components/ProviderAndPayerEditSection";
import { RuleName } from "./components/RuleName";
import { SubmitEditPackages } from "./components/SubmitEditPackages";
import { TriggerCptCodes } from "./components/TriggerCptCodes";
import {
  editFormScope,
  savedApplicabilityOf,
} from "./helpers/ruleApplicability";
import { RuleBucketProvider } from "./hooks/useRuleBucket";

export function EditPackagesPage() {
  useDocumentTitle("Edit Package Rules");
  const { id } = useParams();
  const { data: ruleset, isLoading } = useGetPackageRulesetByIdQuery(id);

  const methods = useForm({
    defaultValues: {
      // CLE-2842 step 2 — applicability, provider, payer (All payers) and a Fallback's payer.
      ...editFormScope(ruleset?.data),
      groupName: ruleset?.data.groupName,
      triggerCptCodes: ruleset?.data.triggerCptCodes,
      bundledCptCodes: ruleset?.data.bundledCptCodes,
      contractLanguage: ruleset?.data.contractLanguage ?? "",
    },
  });

  useEffect(() => {
    if (ruleset) {
      methods.reset({
        ...editFormScope(ruleset?.data),
        groupName: ruleset?.data.groupName,
        triggerCptCodes: ruleset?.data.triggerCptCodes,
        bundledCptCodes: ruleset?.data.bundledCptCodes,
        contractLanguage: ruleset?.data.contractLanguage ?? "",
      });
    }
  }, [ruleset, methods]);

  const savedApplicability = savedApplicabilityOf(ruleset?.data);

  if (isLoading) {
    return <LoadingSpinner />;
  }
  return (
    // CLE-2842 — the row's `bucketMembers` say which other payers share this rule.
    <RuleBucketProvider row={ruleset?.data}>
      <FormProvider {...methods}>
        <Header name={ruleset?.data.groupName} isPackages />
        <ProviderAndPayerEditSection savedApplicability={savedApplicability} />
        <RuleName />
        <TriggerCptCodes />
        <BundledCptCodes />
        <PackageCalculationExamples />
        <ContractLanguageField
          ruleType="PACKAGE"
          ruleId={id}
          attachments={ruleset?.data.attachments}
        />
        {id && ruleset && (
          <SubmitEditPackages id={id} savedApplicability={savedApplicability} />
        )}
        <RuleHistory histories={ruleset?.data.history} />
      </FormProvider>
    </RuleBucketProvider>
  );
}
